import type { OrderFormData, OrderItemBase } from "../../models/OrderModel";

type OrderItem = OrderItemBase & {
  id: number | string;
};

export interface ValidationError {
  field: string;
  message: string;
}

const resolveUnitId = (item: OrderItem): string => {
  const fromUnitObject = item.unit && typeof item.unit === "object" ? item.unit.id : "";
  return String(fromUnitObject || item.unit_id || "");
};

const formatLocalDateTime = (date: any): string | null => {
  if (!date) return null;

  const fromAdapterObject = (() => {
    if (typeof date?.toDate === "function") {
      const value = date.toDate();
      return value instanceof Date ? value : null;
    }
    if (typeof date?.toJSDate === "function") {
      const value = date.toJSDate();
      return value instanceof Date ? value : null;
    }
    if (date?.$d instanceof Date) {
      return date.$d;
    }
    if (typeof date?.valueOf === "function") {
      const timestamp = date.valueOf();
      if (typeof timestamp === "number" && Number.isFinite(timestamp)) {
        const value = new Date(timestamp);
        return Number.isNaN(value.getTime()) ? null : value;
      }
    }
    return null;
  })();

  const value =
    date instanceof Date
      ? date
      : typeof date === "string"
      ? (() => {
          const text = date
            .replace(/\u200e/g, "")
            .replace(/,/g, " ")
            .replace(/\s+/g, " ")
            .trim();
          if (!text) return null;

          const slashMatch = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{1,2}))?$/);
          if (slashMatch) {
            const d = Number(slashMatch[1]);
            const m = Number(slashMatch[2]);
            const y = Number(slashMatch[3]);
            const hh = slashMatch[4] ? Number(slashMatch[4]) : 0;
            const mm = slashMatch[5] ? Number(slashMatch[5]) : 0;
            return new Date(y, m - 1, d, hh, mm, 0, 0);
          }

          const dashDateTime = new Date(text);
          return Number.isNaN(dashDateTime.getTime()) ? null : dashDateTime;
        })()
      : fromAdapterObject;

  if (!value || Number.isNaN(value.getTime())) return null;

  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, "0");
  const d = String(value.getDate()).padStart(2, "0");
  const hh = String(value.getHours()).padStart(2, "0");
  const mm = String(value.getMinutes()).padStart(2, "0");
  const ss = String(value.getSeconds()).padStart(2, "0");
  return `${y}-${m}-${d}T${hh}:${mm}:${ss}`;
};

const formatLocalDate = (date: any): string | null => {
  if (!date) return null;

  const fromAdapterObject = (() => {
    if (typeof date?.toDate === "function") {
      const value = date.toDate();
      return value instanceof Date ? value : null;
    }
    if (typeof date?.toJSDate === "function") {
      const value = date.toJSDate();
      return value instanceof Date ? value : null;
    }
    if (date?.$d instanceof Date) {
      return date.$d;
    }
    if (typeof date?.valueOf === "function") {
      const timestamp = date.valueOf();
      if (typeof timestamp === "number" && Number.isFinite(timestamp)) {
        const value = new Date(timestamp);
        return Number.isNaN(value.getTime()) ? null : value;
      }
    }
    return null;
  })();

  const value =
    date instanceof Date
      ? date
      : typeof date === "string"
      ? (() => {
          const text = date
            .replace(/\u200e/g, "")
            .replace(/,/g, " ")
            .replace(/\s+/g, " ")
            .trim();
          if (!text) return null;

          const slashMatch = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
          if (slashMatch) {
            const d = Number(slashMatch[1]);
            const m = Number(slashMatch[2]);
            const y = Number(slashMatch[3]);
            return new Date(y, m - 1, d, 0, 0, 0, 0);
          }

          const dashMatch = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
          if (dashMatch) {
            const y = Number(dashMatch[1]);
            const m = Number(dashMatch[2]);
            const d = Number(dashMatch[3]);
            return new Date(y, m - 1, d, 0, 0, 0, 0);
          }

          const fallback = new Date(text);
          return Number.isNaN(fallback.getTime()) ? null : fallback;
        })()
      : fromAdapterObject;

  if (!value || Number.isNaN(value.getTime())) return null;

  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, "0");
  const d = String(value.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export const validateOrderForm = (
  formData: OrderFormData,
  orderItems: OrderItem[]
): ValidationError[] => {
  const errors: ValidationError[] = [];

  // Basic validation
  if (!formData.customer_id) {
    errors.push({ field: "customer_id", message: "Vui lòng chọn khách hàng" });
  }

  if (!formData.sales_channel_id) {
    errors.push({ field: "sales_channel_id", message: "Vui lòng chọn kênh bán hàng" });
  }

  if (!formData.sales_employee_id) {
    errors.push({ field: "sales_employee_id", message: "Vui lòng chọn nhân viên bán hàng" });
  }

  if (!formData.status_id) {
    errors.push({ field: "status_id", message: "Vui lòng chọn trạng thái đơn hàng" });
  }

  if (!formData.delivery_date) {
    errors.push({ field: "delivery_date", message: "Vui lòng chọn ngày hẹn giao hàng" });
  }

  if (!formData.delivery_phone) {
    errors.push({ field: "delivery_phone", message: "Vui lòng nhập SĐT nhận hàng" });
  }

  if (!formData.delivery_address_obj) {
    errors.push({ field: "delivery_address_obj", message: "Vui lòng chọn địa chỉ nhận hàng" });
  }

  const orderType = formData.order_type || "main";
  if (orderType === "free_enter" && !(formData.order_code || "").trim()) {
    errors.push({ field: "order_code", message: "Vui lòng nhập mã đơn hàng" });
  }

  if ((orderType === "ps" || orderType === "bh") && !(formData.order_code_parent || "").trim()) {
    errors.push({ field: "order_code_parent", message: "Vui lòng chọn đơn hàng cha" });
  }

  // Validate order items
  const invalidItems: string[] = [];

  orderItems.forEach((item, index) => {
    if (!item.isGroup) {
      const itemNumber = index + 1;

      if (!item.product_name) {
        invalidItems.push(`Sản phẩm ${itemNumber}: Thiếu tên sản phẩm`);
      }
      if (item.quantity === null || item.quantity === undefined || item.quantity < 0) {
        invalidItems.push(`Sản phẩm ${itemNumber}: Số lượng không hợp lệ`);
      }
      if (!resolveUnitId(item)) {
        invalidItems.push(`Sản phẩm ${itemNumber}: Chưa chọn đơn vị tính`);
      }
      if (item.unit_price === null || item.unit_price === undefined || item.unit_price < 0) {
        invalidItems.push(`Sản phẩm ${itemNumber}: Đơn giá không hợp lệ`);
      }
    }
  });

  if (invalidItems.length > 0) {
    errors.push({ field: "orderItems", message: invalidItems.join("\n") });
  }

  // Check if there's at least one valid item (with product name and unit)
  const validItems = orderItems.filter(
    (item) =>
      !item.isGroup &&
      item.product_name &&
      item.quantity !== null &&
      item.quantity !== undefined &&
      item.quantity >= 0 &&
        resolveUnitId(item) &&
      item.unit_price !== null &&
      item.unit_price !== undefined &&
      item.unit_price >= 0
  );

  if (validItems.length === 0) {
    errors.push({ field: "orderItems", message: "Vui lòng thêm ít nhất một sản phẩm" });
  }

  return errors;
};

export const prepareOrderData = (
  formData: OrderFormData,
  orderItems: OrderItem[]
): any => {
  console.log("Preparing order data with formData:", formData);
  const nestedSurvey = (formData as any)?.survey || {};
  const surveyDate = formatLocalDateTime(
    formData.survey_date ??
      nestedSurvey.survey_date ??
      (formData as any).survey_date_text ??
      nestedSurvey.survey_date_text ??
      nestedSurvey.date ??
      null
  );
  const surveyAppointmentDate = formatLocalDateTime(
    formData.survey_appointment_date ??
      nestedSurvey.survey_appointment_date ??
      (formData as any).survey_appointment_date_text ??
      nestedSurvey.survey_appointment_date_text ??
      nestedSurvey.appointment_date ??
      null
  );
  const exportFileDate = formatLocalDate(
    formData.export_file_date ?? (formData as any).export_file_date_text ?? null
  );
  const surveyNotes = (
    formData.survey_notes ?? nestedSurvey.survey_notes ?? nestedSurvey.notes ?? ""
  ).trim();
  const surveyEmployee =
    formData.survey_id ?? nestedSurvey.employee ?? nestedSurvey.survey_id ?? null;

  // Prepare groups and items data
  const groupsData: any[] = [];
  const itemsData: any[] = [];
  let groupIndex = 0;
  let itemIndex = 0;

  orderItems.forEach((item, index) => {
    if (item.isGroup) {
      // This is a group
      groupsData.push({
        ...(item.backend_id ? { id: item.backend_id } : {}),
        frontend_id: item.id,
        name: item.groupName || `NhÃ³m ${groupIndex + 1}`,
        order_index: groupIndex,
      });
      groupIndex++;
    } else {
      // This is an item - only add if it's valid
      if (
        item.product_name &&
        item.quantity !== null &&
        item.quantity !== undefined &&
        item.quantity >= 0 &&
        resolveUnitId(item) &&
        item.unit_price !== null &&
        item.unit_price !== undefined &&
        item.unit_price >= 0
      ) {
        // Find which group this item belongs to
        let currentGroupId = null;
        for (let i = index - 1; i >= 0; i--) {
          if (orderItems[i].isGroup) {
            currentGroupId = orderItems[i].backend_id || orderItems[i].id;
            break;
          }
        }

        itemsData.push({
          ...(item.backend_id ? { id: item.backend_id } : {}),
          product_name: item.product_name,
          color: item.color,
          quantity: item.quantity,
          unit_id: resolveUnitId(item),
          unit_price: item.unit_price,
          total_price: item.total_price,
          item_type_tax: item.item_type_tax || "",
          notes: item.notes || "",
          total_price_not_tax: item.total_price_not_tax || 0,
          group_id: currentGroupId,
          order_index: itemIndex,
        });
        itemIndex++;
      }
    }
  });

  return {
    // Relationships
    customer_id: formData.customer_id,
    sales_channel_id: formData.sales_channel_id,
    sales_employee_id: formData.sales_employee_id,
    company_id: formData.company_id,
    status_id: formData.status_id,
    wallet_id: formData.wallet_id || null,

    // Dates - convert to YYYY-MM-DD format
    sale_date: formData.sale_date
      ? formData.sale_date.toISOString().split("T")[0]
      : null,
    delivery_date: formData.delivery_date
      ? formData.delivery_date.toISOString().split("T")[0]
      : null,
    completion_date: formData.completion_date
      ? formData.completion_date.toISOString().split("T")[0]
      : null,

    // Delivery information
    delivery_phone: formData.delivery_phone,
    delivery_address_id: typeof formData.delivery_address_obj === 'string' 
      ? formData.delivery_address_obj 
      : null,

    // Survey and Tech CNC information
    survey: {
      employee: surveyEmployee,
      survey_date: surveyDate,
      survey_appointment_date: surveyAppointmentDate,
      survey_notes: surveyNotes,
    },
    tech_cnc_id: formData.tech_cnc_id || null,
    export_file_date: exportFileDate,

    // Notes
    notes: formData.notes,

    // Fees
    additional_fee_ids: formData.additional_fee_ids,
    shipping_installation_fee_ids: formData.shipping_installation_fee_ids,

    // Order code fields
    order_code: formData.order_code || null,
    order_code_prefix: formData.order_code_prefix || null,
    order_code_suffix: formData.order_code_suffix || formData.order_code || null,
    order_type: formData.order_type || "main",
    order_code_parent: formData.order_code_parent || null,
    parent_order_id: formData.parent_order_id || null,

    // Direct fee amounts (for survey and shipping fees)
    survey_fee_amount: Number(formData.survey_fee_amount) || 0,
    shipping_installation_fee_amount: Number(formData.shipping_installation_fee_amount) || 0,

    // Groups and items
    groups_data: groupsData,
    items_data: itemsData,
  };
};

